import { BigNumber } from 'ethers';
import { useCallback, useMemo, useState } from 'react';
import { useTransactionAdder } from '../state/transactions/hooks';
import { useDoUpdate, useOnchainInteractionsPoolToken } from './useOnchain';
import { ButtonState } from '../components/InteractionButton';
import { PermitData } from './useApprove';
import { useToNumber } from './useConverter';
import { formatFloat } from '../utils/format';
import useGenericCall from './useGenericCall';
import { useOffchainPoolToken } from './useOffchain';
import { OnchainInteractionsBorrowable, onchain } from 'impermax-sdk';


export default function useBorrow(approvalState: ButtonState, amount: BigNumber, invalidInput: boolean, permitData: PermitData): [ButtonState, () => Promise<void>] {
  const onchainInteractionsBorrowable = useOnchainInteractionsPoolToken() as OnchainInteractionsBorrowable;
  const doUpdate = useDoUpdate();
  const addTransaction = useTransactionAdder();
  const [pending, setPending] = useState<boolean>(false);

  const val = useToNumber(amount);
  const symbol = useGenericCall(useOffchainPoolToken(), poolToken => poolToken.getSymbol());
  const summary = `Borrow ${formatFloat(val)} ${symbol}`;
  
  const borrowState: ButtonState = useMemo(() => {
    if (invalidInput) return ButtonState.Disabled;
    if (approvalState != ButtonState.Done) return ButtonState.Disabled;
    if (pending) return ButtonState.Pending;
    return ButtonState.Ready;
  }, [invalidInput, approvalState, pending, amount]);

  const borrow = useCallback(async (): Promise<void> => {
    if (borrowState !== ButtonState.Ready) return;
    setPending(true);
    try {
      await onchainInteractionsBorrowable.borrow(amount, permitData, (hash: string) => {
        addTransaction({ hash }, { summary });
      });
      doUpdate();
    }
    finally {
      setPending(false);
    }
  }, [borrowState, onchainInteractionsBorrowable, addTransaction, amount, permitData]);

  return [borrowState, borrow];
}